import { Box, Paper, Stack, Typography } from "@mui/material";

import type { ProspectScore, ProspectScoreSummary } from "./model";

function ScoreMeter({ label, score, max, display }: ProspectScore) {
  if (max === undefined) {
    return null;
  }

  const filledPercent = max > 0 ? Math.min(Math.max(score / max, 0), 1) : 0;

  return (
    <Box
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={score}
      aria-valuetext={display}
      sx={{
        height: 10,
        borderRadius: 5,
        overflow: "hidden",
        backgroundColor: "grey.200",
      }}
    >
      <Box
        sx={{
          height: "100%",
          width: `${filledPercent * 100}%`,
          borderRadius: 5,
          backgroundColor: "brand.earthly",
        }}
      />
    </Box>
  );
}

function TotalScore({ total }: { total: ProspectScore }) {
  return (
    <Stack
      data-testid="prospect-total-score"
      spacing={1.5}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: "background.tabCard",
      }}
    >
      <Typography variant="overline" sx={{ lineHeight: 1 }}>
        {total.label}
      </Typography>
      <Typography
        component="p"
        sx={{
          fontSize: "3.5rem",
          fontWeight: 700,
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {total.score}
        {total.max !== undefined && (
          <Typography
            component="span"
            sx={{ fontSize: "1.5rem", color: "text.secondary", ml: 1 }}
          >
            / {total.max}
          </Typography>
        )}
      </Typography>
      <ScoreMeter {...total} />
    </Stack>
  );
}

function CategoryScore({ category }: { category: ProspectScore }) {
  return (
    <Box
      data-testid="prospect-category-score"
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(140px, 180px) minmax(0, 1fr) 72px",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Typography variant="body1">{category.label}</Typography>
      <Box>
        <ScoreMeter {...category} />
      </Box>
      <Typography
        variant="body1"
        sx={{
          textAlign: "right",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {category.display}
      </Typography>
    </Box>
  );
}

function ProspectScores({ total, categories }: ProspectScoreSummary) {
  return (
    <Paper elevation={3} data-testid="scores-section" sx={{ m: 4 }}>
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" align="left" sx={{ mb: 3 }}>
          Scores
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "240px minmax(0, 1fr)",
            },
            gap: 4,
            alignItems: "center",
          }}
        >
          <TotalScore total={total} />
          <Stack spacing={2}>
            {categories.map((category) => (
              <CategoryScore key={category.label} category={category} />
            ))}
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
}

export default ProspectScores;
